import React, {useState} from 'react';
import {useDispatch} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Input from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import {BluePillButton} from '../../common/components/BluePillButton';
import {logIn} from '../../redux/slices/auth';
import SecondaryAppBar from '../../lesson-builder/components/SecondayAppBar';
import {isValidEmail} from '../../helpers';

import baseStyles from '../../css/Base.module.css';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [formErrors, setFormErrors] = useState({}); // eslint-disable-line no-unused-vars
  const [errorDialogMessage, setErrorDialogMessage] = useState(null);

  const dispatch = useDispatch();
  const history = useHistory();
  let location = useLocation();

  function handleEmailChanged(event) {
    setEmail(event.target.value);
  }

  function handlePasswordChanged(event) {
    setPassword(event.target.value);
  }

  function isLoginButtonEnabled() {
    return !!email && isValidEmail(email) && !!password;
  }

  function validateForm() {
    const errors = {};

    if (!email) errors.email = 'This field is required.';
    else if (!isValidEmail(email)) errors.email = 'Invalid email address.';

    if (!password) errors.password = 'This field is required.';

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  }

  async function handleSubmitForm(event) {
    event.preventDefault();
    const isFormValid = validateForm();

    if (isFormValid) {
      let response;

      try {
        response = await dispatch(logIn(email, password));
        console.log(`login response: ${JSON.stringify(response)}`);
      } catch (error) {
        console.log(`login error: ${JSON.stringify(error)}`);

        const {code} = error;
        if (code === 'UserNotFoundException' || code === 'NotAuthorizedException') {
          setErrorDialogMessage('Invalid email or password. Try again.');
        } else if (code === 'UserNotConfirmedException') {
          console.log('UserNotConfirmedException');
          history.push(`/confirm?email=${email}`);
        } else {
          setErrorDialogMessage('An error occurred, try again.');
        }
      }

      if (response?.username) {
        const {from} = location.state || {from: {pathname: '/builder'}};
        history.replace(from);
      }
    }
  }

  const handleClickShowPassword = () => {
    setPasswordVisible(!isPasswordVisible);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  function renderErrorDialog() {
    return (
      <Dialog open={!!errorDialogMessage} onClose={() => setErrorDialogMessage(null)}>
        <DialogTitle>Sign In</DialogTitle>
        <DialogContent>
          <DialogContentText>{errorDialogMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDialogMessage(null)} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      <AppBar position="sticky" className="bg-brandorange">
        <Toolbar variant="dense">
          <Typography variant="h6" className={baseStyles['f-g-1']}>
            Flamingo Learning
          </Typography>
        </Toolbar>
      </AppBar>
      <SecondaryAppBar title="Sign in" />

      {renderErrorDialog()}

      <form className="p-20" onSubmit={handleSubmitForm}>
        <Grid container spacing={3}>
          <Grid item xl={2} lg={4} md={6} sm={8} xs={12}>
            <TextField
              variant="standard"
              margin="dense"
              label="Email"
              onChange={handleEmailChanged}
              value={email}
              autoFocus
              fullWidth
            />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xl={2} lg={4} md={6} sm={8} xs={12}>
            <FormControl fullWidth>
              <InputLabel htmlFor="login-form-password" variant="standard">
                Password
              </InputLabel>
              <Input
                id="login-form-password"
                className={baseStyles['m-t-10']}
                value={password}
                onChange={handlePasswordChanged}
                type={isPasswordVisible ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} size="large">
                      {isPasswordVisible ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>
          </Grid>
        </Grid>

        <Grid container spacing={3} className="m-t-10">
          <Grid container item xl={2} lg={4} md={6} sm={8} xs={12}>
            <Grid item xs={6}>
              <Button onClick={() => history.push('/forgot-password')}>Help! I forgot!</Button>
            </Grid>
            <Grid item xs={6} className={baseStyles['text-align-end']}>
              <BluePillButton type="submit" variant="outlined" disabled={!isLoginButtonEnabled()}>
                LOG IN
              </BluePillButton>
            </Grid>
          </Grid>
        </Grid>

        <div className="m-t-20">
          <Typography variant="h6" className={baseStyles['f-g-1']}>
            Don&apos;t have an account?
          </Typography>
          <Button
            className={`${baseStyles['btn-pill']} m-t-5`}
            onClick={() => history.push('/register')}
            variant="outlined"
          >
            CREATE YOUR ACCOUNT
          </Button>
        </div>
      </form>
    </>
  );
}
